/**
 * "Teach a reply this email" on the Test tab. The tried email, with the client's name swapped for a
 * made-up one, is added to a chosen reply's examples as a new draft of that reply; then the email is
 * tried again with that draft competing, so the effect shows at once. The draft goes live only when
 * someone publishes it from the reply's page.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { wizardMagicEmailApi, wizardMagicEmailKeys, type EmailTestResult, type ReplyListEntry } from '../api/api';
import { errorText, useReplyLibrary } from './use-magic-email-library';

const MADE_UP = { full: 'Alex Chen', first: 'Alex', last: 'Chen' };
const escaped = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The email with the client's name (whole, then each part) swapped for "Alex Chen". */
export function withMadeUpName(email: string, clientName: string | null): string {
  const name = clientName?.trim();
  if (!name) return email;
  const [first, ...rest] = name.split(/\s+/);
  const last = rest[rest.length - 1];
  let out = email.replace(new RegExp(`\\b${escaped(name)}\\b`, 'gi'), MADE_UP.full);
  if (first && first.length > 2) out = out.replace(new RegExp(`\\b${escaped(first)}\\b`, 'gi'), MADE_UP.first);
  if (last && last.length > 2) out = out.replace(new RegExp(`\\b${escaped(last)}\\b`, 'gi'), MADE_UP.last);
  return out;
}

/** The reply most likely meant: the one picked, else the likeliest reply that is not "none". */
function likelyReply(result: EmailTestResult): string {
  if (result.reply) return result.reply.entryId;
  return result.selection.template?.options.find(option => option.value !== 'none_of_these')?.value ?? '';
}

export function useAddExample(input: { result: EmailTestResult; email: string; tryAgainWith: (entryId: string) => void }) {
  const queryClient = useQueryClient();
  const library = useReplyLibrary();
  const [entryId, setEntryId] = useState(() => likelyReply(input.result));
  const [example, setExample] = useState(() => withMadeUpName(input.email, input.result.client.name));

  /** Replies that can be sent, by topic: an example on any other would change nothing. */
  const groups = useMemo(() => {
    const byTopic = new Map<string, ReplyListEntry[]>();
    for (const reply of library.data?.entries ?? []) {
      if (reply.status === 'unavailable') continue;
      byTopic.set(reply.category, [...(byTopic.get(reply.category) ?? []), reply]);
    }
    return [...byTopic.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [library.data]);
  const chosen = library.data?.entries.find(reply => reply.entryId === entryId) ?? null;

  const add = useMutation({
    mutationFn: wizardMagicEmailApi.addExample,
    onSuccess: added => {
      void queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.library() });
      void queryClient.invalidateQueries({ queryKey: wizardMagicEmailKeys.entry(added.entryId) });
      toast.success(added.added ? 'Example added as a draft. Trying the email again…' : 'It already has that example. Trying the email again…');
      if (!added.read) {
        toast.warning('The assistant reads only a reply’s first six examples. Move this one up in the reply’s settings.');
      }
      input.tryAgainWith(added.entryId);
    },
    onError: error => toast.error(`Could not add the example: ${errorText(error)}`),
  });

  return {
    groups,
    entryId,
    setEntryId,
    chosen,
    example,
    setExample,
    canAdd: !!entryId && example.trim().length > 0 && !add.isPending,
    addIt: () => add.mutate({ entryId, email: example.trim() }),
    adding: add.isPending,
  };
}
